<template>
  <BaseCard>
    <div>
      <!-- Header -->
      <div class="flex items-center justify-between p-3.5 px-5 border-b border-border-normal" style="height: 72px">
        <!-- left container -->
        <div class="flex">
          <!-- Icon -->
          <div style="width: 45px;" class="icon-shadow mr-3 p-2.5 rounded-full flex items-center justify-center">
            <img src="../../assets/icons/layout-icon.svg">
          </div>
          <!-- Text -->
          <div>
            <!-- Title Container -->
            <div class="flex gap-1">
              <!-- Title -->
              <BaseText type="label" size="md">
                <span class="text-text-muted">
                  Storyboard
                </span>
              </BaseText>
            </div>
            <!-- SubTitle -->
            <BaseText type="body" size="sm">
              <span class="text-text-normal">
                Layout your ad scene-by-scene.
              </span>
            </BaseText>
            <div />
          </div>
        </div>

        <!-- Right Container -->
        <BaseSelect item-value="title" popper-center :items="getTemplates">
          <div slot="reference">
            <BaseButton outlined>
              <div class="flex items-center gap-1.5 -mx-2">
                <img src="../../assets/icons/template.svg">

                <BaseText type="body" size="sm" class="text-text-muted">
                  Templates
                </BaseText>
                <img class="ml-auto" src="../../assets/icons/chevron-down-small.svg">
              </div>
            </BaseButton>
          </div>

          <template #item="{ item }">
            <div class="flex w-full h-full" @click="item.action">
              <BaseText type="body" size="sm" class="text-text-muted">
                {{ item.title }}
              </BaseText>
            </div>
          </template>
        </BaseSelect>
      </div>

      <!-- Settings Bar -->
      <div class="py-3 px-5">
        <!-- Left Side -->
        <div class="flex text-text-muted gap-3">
          <!-- Word Count -->
          <div class="px-2 py-1.5 border border-border-normal rounded-md flex gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.77222 16.8757V9.1665M18.5416 3.95898H8.12492M10.2083 9.16732H1.45825M13.3333 4.16732V16.8757"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

            <BaseText type="body" size="sm">
              {{ wordCount || 0 }} Words
            </BaseText>
          </div>
          <!-- Estimated Duration -->
          <div class="px-2 py-1.5 border border-border-normal rounded-md flex gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0001 6.45768V9.99935L12.2917 12.291M17.7084 9.99935C17.7084 14.2565 14.2573 17.7077 10.0001 17.7077C5.74289 17.7077 2.29175 14.2565 2.29175 9.99935C2.29175 5.74215 5.74289 2.29102 10.0001 2.29102C14.2573 2.29102 17.7084 5.74215 17.7084 9.99935Z"
                stroke="#5E6678" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

            <BaseText type="body" size="sm">
              {{ estimatedSpeechTime || '00:00 Duration' }}
            </BaseText>
          </div>
          <div class="ml-auto">
            <div class="flex items-center -mx-2 rounded-full w-32 bg-background-normal p-1">
              <div
                class="flex items-center justify-center w-1/3 h-full rounded-full cursor-pointer py-0.5 transition duration-75 ease-in-out"
                :class="{
                  'shadow bg-white text-neutral-900': view === 'drag',
                  'text-neutral-500 shadow-none': view !== 'drag'
                }" @click="view = 'drag'">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M4.03502 16.6933L4.37551 16.0251L4.37551 16.0251L4.03502 16.6933ZM3.30666 15.965L3.97491 15.6245L3.97491 15.6245L3.30666 15.965ZM16.6933 15.965L16.0251 15.6245L16.0251 15.6245L16.6933 15.965ZM15.965 16.6933L15.6245 16.0251L15.6245 16.0251L15.965 16.6933ZM16.6933 4.03502L16.0251 4.37551L16.0251 4.37551L16.6933 4.03502ZM15.965 3.30666L15.6245 3.97491L15.6245 3.97491L15.965 3.30666ZM3.30666 4.03502L2.6384 3.69452L2.6384 3.69452L3.30666 4.03502ZM4.03502 3.30666L3.69452 2.6384L3.69452 2.6384L4.03502 3.30666ZM10.75 3.125V2.375H9.25V3.125H10.75ZM3.125 9.25H2.375V10.75H3.125V9.25ZM9.25 16.875V17.625H10.75V16.875H9.25ZM16.875 10.75H17.625V9.25H16.875V10.75ZM16.125 5.79167V14.2083H17.625V5.79167H16.125ZM14.2083 16.125H5.79167V17.625H14.2083V16.125ZM3.875 14.2083V5.79167H2.375V14.2083H3.875ZM5.79167 3.875H14.2083V2.375H5.79167V3.875ZM5.79167 16.125C5.31258 16.125 4.99837 16.1244 4.75808 16.1048C4.52654 16.0859 4.43042 16.0531 4.37551 16.0251L3.69452 17.3616C3.99613 17.5153 4.31163 17.5733 4.63593 17.5998C4.95147 17.6256 5.33733 17.625 5.79167 17.625V16.125ZM2.375 14.2083C2.375 14.6627 2.37442 15.0485 2.4002 15.3641C2.42669 15.6884 2.48472 16.0039 2.6384 16.3055L3.97491 15.6245C3.94693 15.5696 3.91413 15.4735 3.89522 15.2419C3.87558 15.0016 3.875 14.6874 3.875 14.2083H2.375ZM4.37551 16.0251C4.20303 15.9372 4.0628 15.797 3.97491 15.6245L2.6384 16.3055C2.87009 16.7602 3.2398 17.1299 3.69452 17.3616L4.37551 16.0251ZM16.125 14.2083C16.125 14.6874 16.1244 15.0016 16.1048 15.2419C16.0859 15.4735 16.0531 15.5696 16.0251 15.6245L17.3616 16.3055C17.5153 16.0039 17.5733 15.6884 17.5998 15.3641C17.6256 15.0485 17.625 14.6627 17.625 14.2083H16.125ZM14.2083 17.625C14.6627 17.625 15.0485 17.6256 15.3641 17.5998C15.6884 17.5733 16.0039 17.5153 16.3055 17.3616L15.6245 16.0251C15.5696 16.0531 15.4735 16.0859 15.2419 16.1048C15.0016 16.1244 14.6874 16.125 14.2083 16.125V17.625ZM16.0251 15.6245C15.9372 15.797 15.797 15.9372 15.6245 16.0251L16.3055 17.3616C16.7602 17.1299 17.1299 16.7602 17.3616 16.3055L16.0251 15.6245ZM17.625 5.79167C17.625 5.33733 17.6256 4.95147 17.5998 4.63593C17.5733 4.31163 17.5153 3.99613 17.3616 3.69452L16.0251 4.37551C16.0531 4.43042 16.0859 4.52654 16.1048 4.75808C16.1244 4.99837 16.125 5.31258 16.125 5.79167H17.625ZM14.2083 3.875C14.6874 3.875 15.0016 3.87558 15.2419 3.89522C15.4735 3.91413 15.5696 3.94693 15.6245 3.97491L16.3055 2.6384C16.0039 2.48472 15.6884 2.42669 15.3641 2.4002C15.0485 2.37442 14.6627 2.375 14.2083 2.375V3.875ZM17.3616 3.69452C17.1299 3.2398 16.7602 2.87009 16.3055 2.6384L15.6245 3.97491C15.797 4.0628 15.9372 4.20303 16.0251 4.37551L17.3616 3.69452ZM3.875 5.79167C3.875 5.31258 3.87558 4.99837 3.89522 4.75808C3.91413 4.52654 3.94693 4.43042 3.97491 4.37551L2.6384 3.69452C2.48472 3.99613 2.42669 4.31163 2.4002 4.63593C2.37442 4.95147 2.375 5.33733 2.375 5.79167H3.875ZM5.79167 2.375C5.33733 2.375 4.95147 2.37442 4.63593 2.4002C4.31163 2.42669 3.99613 2.48472 3.69452 2.6384L4.37551 3.97491C4.43042 3.94693 4.52654 3.91413 4.75808 3.89522C4.99837 3.87558 5.31258 3.875 5.79167 3.875V2.375ZM3.97491 4.37551C4.06279 4.20303 4.20303 4.06279 4.37551 3.97491L3.69452 2.6384C3.2398 2.87009 2.87009 3.2398 2.6384 3.69452L3.97491 4.37551ZM9.25 3.125V10H10.75V3.125H9.25ZM10 9.25H3.125V10.75H10V9.25ZM9.25 10V16.875H10.75V10H9.25ZM10 10.75H16.875V9.25H10V10.75Z"
                    fill="currentColor" />
                </svg>
              </div>
              <div
                class="flex items-center justify-center w-1/3 h-full rounded-full cursor-pointer py-0.5 transition duration-75 ease-in-out"
                :class="{
                  'shadow bg-white text-neutral-900': view === 'list',
                  'text-neutral-500 shadow-none': view !== 'list'
                }" @click="view = 'list'">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
                  class="h-5">
                  <path
                    d="M9.79167 14.7915H16.875M10 4.99984H17.0833M6.45833 5.20817C6.45833 6.12865 5.71214 6.87484 4.79167 6.87484C3.87119 6.87484 3.125 6.12865 3.125 5.20817C3.125 4.2877 3.87119 3.5415 4.79167 3.5415C5.71214 3.5415 6.45833 4.2877 6.45833 5.20817ZM6.45833 14.7915C6.45833 15.712 5.71214 16.4582 4.79167 16.4582C3.87119 16.4582 3.125 15.712 3.125 14.7915C3.125 13.871 3.87119 13.1248 4.79167 13.1248C5.71214 13.1248 6.45833 13.871 6.45833 14.7915Z"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
              </div>
              <div
                class="flex items-center justify-center w-1/3 h-full rounded-full cursor-pointer py-0.5 transition duration-75 ease-in-out"
                :class="{
                  'shadow bg-white text-neutral-900': view === 'scene',
                  'text-neutral-500 shadow-none': view !== 'scene'
                }" @click="view = 'scene'">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M3.125 15.2082H6.04167M3.125 9.99984H7.70833M3.125 4.7915H16.875M14.1667 8.74984L15.4167 11.2498L17.9167 12.4998L15.4167 13.7498L14.1667 16.2498L12.9167 13.7498L10.4167 12.4998L12.9167 11.2498L14.1667 8.74984Z"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side -->
      </div>

      <!-- Storyboard -->
      <div>
        <StoryDragView v-if="view === 'drag'" :brief="brief" show-actions :loading-references="loadingReferences"
          :ai-brief="aiBrief" show-copy show-reference @openAiModal="openAiModal" @update="storyUpdate"
          @addPanel="addPanel" />
        <StoryListView v-if="view === 'list'" :brief="brief" show-actions :loading-references="loadingReferences"
          :ai-brief="aiBrief" show-copy show-reference @openAiModal="openAiModal" @update="storyUpdate"
          @addPanel="addPanel" />
        <StorySceneView v-if="view === 'scene'" :brief="brief" show-actions :loading-references="loadingReferences"
          :ai-brief="aiBrief" show-copy show-reference @openAiModal="openAiModal" @update="storyUpdate"
          @addPanel="addPanel" />
      </div>

      <!-- Modals -->
      <AiRewriteModal v-if="showAiRewriteModal" :rewrite-section="rewriteSection" :panels="panels"
        @close="showAiRewriteModal = false" @replace-copy="replaceCopy" />

      <ConfirmDeleteModal v-if="showConfirmModal" template @confirm="updateSelectedTemplate"
        @close="handleCancelChange" />
    </div>
  </BaseCard>
</template>
<script>
import StoryDragView from '../briefs/editForm/story/StoryDragView.vue'
import StoryListView from '../briefs/editForm/story/StoryListView.vue'
import StorySceneView from '../briefs/editForm/story/StorySceneView.vue'
import AiRewriteModal from '../briefs/editForm/story/AiRewriteModal.vue'
import storyTemplates from '../briefs/editForm/storyTemplates'
import ConfirmDeleteModal from '../globals/ConfirmDeleteModal.vue'
import _ from "lodash";

export default {
  name: 'StoryBoard',
  components: {
    AiRewriteModal,
    StoryDragView,
    StoryListView,
    StorySceneView,
    ConfirmDeleteModal
  },
  props: {
    brief: {
      type: Object,
      required: true
    },
    loadingReferences: {
      type: Boolean,
      default: () => true
    },
    aiBrief: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      // Data
      panels: [],
      template: [{ title: 'Standard' }],
      storyTemplates: storyTemplates,

      // Stats
      estimatedSpeechTime: null,
      wordCount: null,

      // Settings
      rewriteSection: {},
      showAiRewriteModal: false,

      // Confirm Modal
      showConfirmModal: false,

      // Togggle View
      view: 'drag'
    }
  },
  computed: {
    getTemplates() {
      return Object.keys(this.storyTemplates.templates).map((key) => ({
        title: key,
        action: () => {
          this.newTemplate = [{ title: key }]
          this.showConfirmModal = this.checkUpdateSelectedTemplate()

          if (!this.showConfirmModal) {
            this.updateSelectedTemplate()
          }
        }
      }))
    }
  },
  watch: {
    lastUpdated: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.brief.story) {
          this.panels = this.brief.story

          // Check what the last set view was
          if (this.brief.storyView) {
            this.view = this.brief.storyView
          }
        }
      }
    }
  },
  mounted() {
    this.panels = this.brief.story
    this.estimatedSpeechTime = this.calculateSpeechTimeRangeFromScenes(this.panels) || 0
    this.wordCount = this.panels.map(scene => scene.copy).join(' ').split(' ').length || 0
  },
  methods: {
    openAiModal(index, section) {
      // Get the section value
      const copy = this.panels[index][section]

      this.rewriteSection = {
        index,
        section,
        copy
      }

      this.showAiRewriteModal = true
    },
    addPanel() {
      console.log('add panel')
      if (!this.brief.story) {
        this.panels = this.brief.story = [{
          scene: [{ title: '' }],
          copy: '',
          visuals: ''
        }]

        return
      }
      this.panels = this.brief.story.concat({
        scene: [{ title: '' }],
        copy: '',
        visuals: ''
      })

      this.emitUpdate()
    },
    storyUpdate(storyCopy) {

      this.panels = storyCopy
      // Debounce word count and time range by a small number
      if (this.debounceUpdate) {
        this.debounceUpdate.cancel();
      }

      this.debounceUpdate = _.debounce(() => {
        this.estimatedSpeechTime = this.calculateSpeechTimeRangeFromScenes(storyCopy) || 0
        // storyCopy contains new lines 
        const cleanWords = this.panels.map(scene => scene.copy).join(' ').replace(/\n/g, ' ').trim().split(/\s+/).filter(Boolean)
        this.wordCount = cleanWords.length || 0
      }, 100);

      this.debounceUpdate();

      // Emit update is debounced from parent
      this.emitUpdate()
    },
    deleteRow(index) {
      // Delete previous reference
      this.deleteReference(index, true)

      this.panels = this.panels.filter((field, i) => i !== index)

      this.emitUpdate()
    },
    calculateSpeechTimeRangeFromScenes(scenes) {
      // Average speaking rate is 125 words per minute
      const averageWordsPerMinute = 125

      // Extract the script copy from each scene and join them into a single string
      const scriptCopy = scenes.map(scene => scene.copy).join(' ')

      // Estimate the number of words in the script copy by splitting it into an array
      const words = scriptCopy.split(' ')
      const wordCount = words.length

      // Calculate the estimated speaking time range in seconds
      const minimumTime = Math.round(wordCount / averageWordsPerMinute * 60)
      const maximumTime = Math.round((wordCount / averageWordsPerMinute + 0.5) * 60)

      // Format the response in the desired format
      const minimumTimeString = new Date(minimumTime * 1000).toISOString().substr(14, 5)
      const maximumTimeString = new Date(maximumTime * 1000).toISOString().substr(14, 5)
      const durationString = `${minimumTimeString} - ${maximumTimeString} Duration`

      return durationString
    },
    replaceCopy(newValue, index, section) {
      const copyArr = [...this.panels]
      copyArr[index][section] = newValue

      this.panels = copyArr

      // Flash the element with the green text animation
      const elem = document.getElementById(`${section}-${index}`)
      elem.classList.add('flash-green')

      // Remove the animation after 1 second
      setTimeout(() => {
        elem.classList.remove('flash-green')
      }, 1000)

      this.emitUpdate()
    },
    emitUpdate() {
      if (this.shared) return
      this.$emit('saveSection', {
        field: 'story',
        value: this.panels,
        shouldWait: true
      })

      // this.calculateSpeechTimeRangeFromScenes(this.panels)
    },
    handleCancelChange() {
      this.showConfirmModal = false
    },
    checkUpdateSelectedTemplate() {
      return this.panels.find(
        (panel) => panel.copy.length > 0 || panel.visuals.length > 0
      )
    },
    updateSelectedTemplate() {
      this.template = this.newTemplate
      const value = this.template[0]

      this.estimatedSpeechTime = 0
      this.wordCount = 0

      this.panels = this.storyTemplates.templates[value.title].map((template) => {
        return {
          scene: [template],
          copy: '',
          visuals: ''
        }
      })

      this.emitUpdate()
      this.showConfirmModal = false
    }
  }
}
</script>
